import React, { useCallback, useContext, useRef, useState } from 'react'
import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import Expander from 'components/utils/Expander'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import useCarHireLabels from 'hooks/CarHire/useCarHireLabels'
import CarHireSearchDriversAgeCategorySelect from '../CarHireSearchBar/CarHireSearchDriversAgeCategorySelect'
import CarHireSearchDrawer from './CarHireSearchDrawer'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { DMY_CASUAL_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { shortTimeHourFormatAmPm } from 'lib/datetime/dateUtils'
import useToggle from 'hooks/useToggle'
import CarHireTimeSelect from '../CarHireSearchBar/CarHireTimeSelect'
import { pushWithRegion } from 'actions/NavigationActions'
import qs from 'qs'

import { useAppDispatch } from 'hooks/reduxHooks'
import CarHireDriversAgeInput from '../CarHireSearchBar/CarHireDriversAgeInput'
import { getCarHireLocationId, getCarHireSearchType } from '../carHireUtils'
import { useIntl } from 'react-intl'

export enum searchSteps {
  LOCATION = 'location',
  SECONDARY_LOCATION = 'secondaryLocation',
  DATE = 'date',
  PICKUP_TIME = 'pickUpTime',
  RETURN_TIME = 'returnTime',
}

// Margin doesn't animate on slide down so we're left with
// double gap when this is closed. So fake it with a padding top instead
// this padding will get animated correctly
const SlideWrapper = styled(Expander)`
  margin: 0;

  >:first-child {
    padding-top: ${rem(12)};
  }
`

const DriversAgeInput = styled(CarHireDriversAgeInput)`
  width: ${rem(118)};
`

const DriversAgeContainer = styled.div`
  opacity: 1;
  transition: opacity 0.2s;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

function CarHireMobileSearchControls() {
  const {
    searchItem,
    secondarySearchItem,
    checkinDate: pickUpDate,
    checkoutDate: dropOffDate,
    pickUpTime,
    dropOffTime,
    driversAgeCategory,
    driversAge,
  } = useContext(GlobalSearchStateContext)

  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const dispatch = useAppDispatch()

  const pickUpTimeFieldRef = useRef<HTMLButtonElement>(null)
  const dropOffTimeFieldRef = useRef<HTMLButtonElement>(null)
  const needsActualAge = driversAgeCategory === 1 || driversAgeCategory === 3

  const [activeStep, setActiveStep] = useState<searchSteps | null>(null)
  const [showPickUpTimeSelect, toggleShowPickUpTimeSelect] = useToggle(false)
  const [showDropOffTimeSelect, toggleShowDropOffTimeSelect] = useToggle(false)
  const [differentLocations, setDifferentLocations] = useState<boolean>(!!(secondarySearchItem && searchItem?.format.mainText !== secondarySearchItem?.format.mainText))

  const openPickUpSearchStep = useCallback(() => setActiveStep(searchSteps.LOCATION), [setActiveStep])
  const openDropOffSearchStep = useCallback(() => setActiveStep(searchSteps.SECONDARY_LOCATION), [setActiveStep])
  const openDateSearchStep = useCallback(() => setActiveStep(searchSteps.DATE), [setActiveStep])

  const onSelectDriversAge = useCallback((nextCategory: App.CarHireDriverAgeCategory) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_DRIVERS_AGE_CATEGORY, driversAgeCategory: nextCategory })
  }, [searchDispatch])

  const setPickUpTimeDispatch = useCallback((time: string) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_PICKUP_TIME, pickupTime: time })
    toggleShowPickUpTimeSelect()
  }, [searchDispatch, toggleShowPickUpTimeSelect])

  const setDropOffTimeDispatch = useCallback((time: string) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_RETURN_TIME, dropOffTime: time })
    toggleShowDropOffTimeSelect()
  }, [searchDispatch, toggleShowDropOffTimeSelect])

  const onDifferentLocationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget.checked
    if (!nextValue) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_SECONDARY_SEARCH_ITEM })
    }
    setDifferentLocations(nextValue)
  }, [searchDispatch])

  const intl = useIntl()
  const {
    pickupDropoffLocationSearchTitleLabel,
    pickupLocationSearchTitleLabel,
    dropoffLocationSearchTitleLabel,
    pickupLocationSearchPlaceholderLabel,
  } = useCarHireLabels()

  const differentLocationLabel = intl.formatMessage({
    id: 'SSuwI7M-hZqL920sYJpI',
    defaultMessage: 'Drop-off at a different location',
    description: 'Checkbox label to enable different drop-off location for car hire search',
  })

  const pickUpDateLabel = intl.formatMessage({
    id: 'QevHI935af3iegseeTip',
    defaultMessage: 'Pick-up date',
    description: 'Label for car hire pick-up date field in the date selection or calendar picker',
  })

  const startDatePlaceholder = intl.formatMessage({
    id: 'DEh-W2wjp2_vhxrVlxpW',
    defaultMessage: 'Start date',
    description: 'Placeholder for car hire pick-up date field in car hire section',
  })

  const timeLabel = intl.formatMessage({
    id: 'GNTnJP0h0IMl-XYzxyCy',
    defaultMessage: 'Time',
    description: 'Label for car hire time selection field in the car hire booking section',
  })

  const startTimePlaceholder = intl.formatMessage({
    id: 'S4Myn7A1DF0dtzEuCCD4',
    defaultMessage: 'Start time',
    description: 'Placeholder for car hire pick-up time field in car hire section',
  })

  const dropOffDateLabel = intl.formatMessage({
    id: 'YomcnCUa97-rCn5AKpM-',
    defaultMessage: 'Drop-off date',
    description: 'Label for car hire drop-off date field in the date selection or calendar picker',
  })

  const endDatePlaceholder = intl.formatMessage({
    id: 'bmcV-bjKZVe3FUTlsD4k',
    defaultMessage: 'End date',
    description: 'Placeholder for car hire drop-off date field in car hire section',
  })

  const endTimePlaceholder = intl.formatMessage({
    id: 'OCbg4CNoxXSLAigwIbfR',
    defaultMessage: 'End time',
    description: 'Placeholder for car hire drop-off time field in car hire section',
  })

  const driversAgeQuestion = intl.formatMessage({
    id: 'QziN_mgALQkd2KjTYzWe',
    defaultMessage: 'How old is the driver?',
    description: 'Label asking for the car hire driver age in car hire section',
  })

  const searchLabel = intl.formatMessage({
    id: 'XMPUXJ72MXpRKOpyx2wn',
    defaultMessage: 'Search',
    description: 'Submit button text for car hire search form in the search interface',
  })

  const onSubmit = useCallback(() => {
    const pickUpItem = searchItem
    const dropOffItem = secondarySearchItem ?? searchItem

    const query = qs.stringify({
      pickUpName: pickUpItem?.format.mainText,
      dropOffName: dropOffItem?.format.mainText,
      pickUpId: pickUpItem ? getCarHireLocationId(pickUpItem) : undefined,
      dropOffId: dropOffItem ? getCarHireLocationId(dropOffItem) : undefined,
      pickUpDate: pickUpDate?.format(ISO_DATE_FORMAT),
      pickUpTime,
      dropOffDate: dropOffDate?.format(ISO_DATE_FORMAT),
      dropOffTime,
      ageCategory: driversAgeCategory,
      age: driversAge,
      pickUpSearchType: pickUpItem ? getCarHireSearchType(pickUpItem) : undefined,
      dropOffSearchType: dropOffItem ? getCarHireSearchType(dropOffItem) : undefined,
    })

    dispatch(pushWithRegion('/search/car-hire', query))
  }, [dispatch, driversAge, driversAgeCategory, dropOffDate, dropOffTime, pickUpDate, pickUpTime, searchItem, secondarySearchItem])

  return (
    <>
      {!!activeStep &&
        <CarHireSearchDrawer
          differentLocations={differentLocations}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          onSubmit={onSubmit}
        />}

      <VerticalSpacer gap={16} as="form" onSubmit={onSubmit}>
        <SearchFormField
          label={differentLocations ?
            pickupLocationSearchTitleLabel :
            pickupDropoffLocationSearchTitleLabel}
          placeholder={pickupLocationSearchPlaceholderLabel}
          value={searchItem?.format?.mainText}
          onClick={openPickUpSearchStep}
          required
        />

        <SlideWrapper expanded={differentLocations}>
          <SearchFormField
            label={dropoffLocationSearchTitleLabel}
            placeholder={pickupLocationSearchPlaceholderLabel}
            value={secondarySearchItem?.format.mainText}
            onClick={openDropOffSearchStep}
            required={differentLocations}
          />
        </SlideWrapper>

        <CheckboxInput
          defaultChecked={differentLocations}
          onChange={onDifferentLocationChange}
        >
          {differentLocationLabel}
        </CheckboxInput>

        <VerticalSpacer gap={16}>
          <SearchFormFieldGroup>
            <SearchFormField
              label={pickUpDateLabel}
              value={pickUpDate ? pickUpDate.format(DMY_CASUAL_FORMAT) : undefined}
              placeholder={startDatePlaceholder}
              onClick={openDateSearchStep}
              required
            />
            <div style={{ position: 'relative' }}>
              <SearchFormField
                ref={pickUpTimeFieldRef}
                label={timeLabel}
                placeholder={startTimePlaceholder}
                value={pickUpTime}
                displayValue={pickUpTime ? shortTimeHourFormatAmPm(new Date('1970-01-01T' + pickUpTime + 'Z'), true) : undefined}
                onClick={toggleShowPickUpTimeSelect}
                required
              />
              <CarHireTimeSelect
                open={showPickUpTimeSelect}
                toggleDropDown={toggleShowPickUpTimeSelect}
                dropdownTriggerRef={pickUpTimeFieldRef}
                onChange={setPickUpTimeDispatch}
                selectedTime={pickUpTime}
              />
            </div>
          </SearchFormFieldGroup>

          <SearchFormFieldGroup>
            <SearchFormField
              label={dropOffDateLabel}
              value={dropOffDate ? dropOffDate.format(DMY_CASUAL_FORMAT) : undefined}
              placeholder={endDatePlaceholder}
              onClick={openDateSearchStep}
              required
            />
            <div style={{ position: 'relative' }}>
              <SearchFormField
                ref={dropOffTimeFieldRef}
                label={timeLabel}
                placeholder={endTimePlaceholder}
                value={dropOffTime}
                displayValue={dropOffTime ? shortTimeHourFormatAmPm(new Date('1970-01-01T' + dropOffTime + 'Z'), true) : undefined}
                onClick={toggleShowDropOffTimeSelect}
                required
              />
              <CarHireTimeSelect
                open={showDropOffTimeSelect}
                toggleDropDown={toggleShowDropOffTimeSelect}
                dropdownTriggerRef={dropOffTimeFieldRef}
                onChange={setDropOffTimeDispatch}
                selectedTime={dropOffTime}
              />
            </div>
          </SearchFormFieldGroup>

          <CarHireSearchDriversAgeCategorySelect
            value={driversAgeCategory}
            onChange={onSelectDriversAge}
          />

          <SlideWrapper expanded={needsActualAge}>
            <DriversAgeContainer className={clsx({ hidden: !needsActualAge })}>
              <VerticalSpacer as="label" gap={8}>
                <Caption variant="large">
                  {driversAgeQuestion}
                </Caption>
                <DriversAgeInput />
              </VerticalSpacer>
            </DriversAgeContainer>
          </SlideWrapper>

        </VerticalSpacer>
        <TextButton fit="mobile-full-width" kind="primary" type="submit" size="large">{searchLabel}</TextButton>
      </VerticalSpacer>
    </>
  )
}

export default CarHireMobileSearchControls
